import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { NumericFormat } from "react-number-format";
import { percentageValue } from "_shared/percentages/percent";
import { Masked } from "work/masked-fields";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { ArchivedFieldHistoryButton } from "work/proposals/_views/archive/archived-field-history";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import { workPaneProseInset } from "work/_views/work-pane-layout";

// One field and no table, so all of it is prose — see `workPaneProseInset`. The margin here is
// vertical only, so with the tab's row running flush the field sat against the navigation drawer;
// the draft and active discount tabs have a margin on all four sides and never did.
const ComponentContainer = styled("div")(({ theme }) => ({
  ...workPaneProseInset(theme),
  height: "100%",
  margin: theme.spacing(2, 0, 4, 0),
}));
// The field keeps its own width, with its history beside it.
const DiscountRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(0.5),
}));

type DiscountTabProps = {
  proposal: Proposal;
};

export default function DiscountTab(props: Readonly<DiscountTabProps>) {
  const { proposal } = props;

  // Declining is not the same as showing zero. Falling back to a default here rendered "0.00%" to a
  // reader who may not see the discount at all — a plausible, wrong statement about terms that were
  // actually hired, which is worse than an obvious refusal. Every sibling tab on this page declines;
  // this one substituted a value instead.
  if (Masked.isMasked(proposal.discount)) {
    return maskedProposalFieldNotice(MaskedProposalField.Discount);
  }

  const discount = proposal.discount?.value ?? 0;

  return (
    <ComponentContainer>
      <DiscountRow>
        <NumericFormat
          name="discount"
          label="Discount"
          customInput={TextField}
          allowNegative={false}
          value={percentageValue(discount)}
          suffix="%"
          type="text"
          thousandSeparator={false}
          decimalScale={2}
          disabled={true}
        />
        <ArchivedFieldHistoryButton field={ProposalField.Discount} />
      </DiscountRow>
    </ComponentContainer>
  );
}
